import { CartService } from '@b2b/common/services';
import { ApiService } from '@b2b/common/services/api/types';
import { CalendarService } from '@b2b/common/services/calendar';
import { TODO } from '@b2b/types/core';
import angular, { IQService } from 'angular';

export class PrivateEventService {
  static $inject = [
    '$q',
    'ApiService',
    'HelpersService',
    'CartService',
    'CalendarService',
    'TemplatesHelpersService',
    'PRIVATE_EVENT_EXPIRE_OPTIONS',
  ];

  constructor(
    private $q: IQService,
    private apiService: ApiService,
    private helpersService: TODO,
    private cartService: CartService,
    private calendarService: CalendarService,
    private templatesHelpersService: TODO,
    private PRIVATE_EVENT_EXPIRE_OPTIONS: TODO,
  ) {}

  getPriceLists() {
    return this.apiService.price.lists.list(this.helpersService.getPaginator());
  }

  getDiscounts() {
    return this.apiService.discounts.list('v2', this.helpersService.getPaginator());
  }

  /**
   * Get template names for the dropdown
   */
  getTemplateNames(isPrivate: boolean, search?: string) {
    const props = {
      page: 1,
      pageSize: 10,
      sortDirection: 'asc',
      sortField: 'name',
      totalItems: 0,
      type: '',
      search,
    };

    return this.apiService.program.names(isPrivate, props).then((response) => {
      response.data.payload.list = response.data.payload.list.map((item) => {
        if (item.colorCategory && item.colorCategory.textColor) {
          item.colorCategoryStyle = this.templatesHelpersService.getColorCategoryStyle(item.colorCategory);
        }

        return item;
      });

      return response;
    });
  }

  /**
   * Get event template by its id
   * Used in template list dropdown component
   */
  getTemplate(id: number) {
    return this.apiService.program.get(id);
  }

  /**
   * Get discount of template through template and discount relationship
   */
  getEventTemplateDetail(templateId: string) {
    return this.apiService.partners.templates.detail(templateId);
  }

  /**
   * Get the event template discounted price list
   */
  getEventTemplateDiscountedPriceList(discountId: TODO, priceListId: TODO) {
    return this.apiService.discounts.preview({ discountId: discountId, priceListId: priceListId });
  }

  /**
   * Get instance of the template by its id
   * @param eventId An event Id passed to the modal on edit
   */
  getEventInstance(eventId: string) {
    const deferred = this.$q.defer<TODO>();

    this.apiService.event
      .detail(eventId)
      .then((res) => {
        const event = res.data.payload;

        // get even more info from the event template
        this.apiService.program
          .get(event.templateId)
          .then((res) => {
            const data = (res || {}).data || {};
            const template = data.payload || {};

            event.schedules = template.schedules;

            const priceLists = template.priceLists || [];
            event.priceList = priceLists[0] || event.priceList;

            deferred.resolve({ event: event, template: template });
          })
          .catch((res) => {
            console.error(res);
          });
      })
      .catch((res) => {
        console.error(res);
      });

    return deferred.promise;
  }

  /**
   * @deprecated
   *
   * @param templateId
   * @return {*}
   */
  getEventTemplatePublished(templateId) {
    return this.apiService.program.template.published({ eventTemplateId: templateId });
  }

  /**
   * Update event with payload constructed from the model object
   *
   * @param model
   * @param event
   * @return promise
   */
  updateEventAndTemplate(model, event, template) {
    const deferred = this.$q.defer<TODO>();

    event.name = model.name;
    event.description = model.description;
    event.capacity = model.capacity;
    event.emailConfirmationMessage = model.emailConfirmationMessage;
    event.ticketDetails.ticketDurationDays = model.expireDays;
    event.ticketDetails.expireFromEventDate = model.expireOption === this.PRIVATE_EVENT_EXPIRE_OPTIONS[0];
    event.colorCategory = model.colorCategory;

    this.apiService.program.update(template, false).then(() => {
      this.apiService.event.update(event).then(() => {
        deferred.resolve();
      });
    });
    return deferred.promise;
  }

  /**
   * Add a template event to the cart
   *   1. Create a template and publish an event
   *   2. Get the published event by the template id
   *   3. Get the event object needed by the cart
   *   4. return the event object
   * @param template Template payload
   * @param accountId account id
   * @param accountCategoryRelation account category relation
   * @returns A promise object
   */
  addToOrderCheckout(template: TODO, accountId: number | null, accountCategoryRelation: TODO) {
    const deferred = this.$q.defer<TODO>();

    if (template.id && template.scheduleId) {
      this.apiService.event
        .createFromPrivateSchedule(template)
        .then((response) => {
          const res = response;

          if (
            template.schedules[0].hasOwnProperty('resourceRequest') &&
            template.schedules[0].resourceRequest instanceof Array &&
            template.schedules[0].resourceRequest.length
          ) {
            // we also need to push all resource requests one by one
            template.schedules[0].resourceRequest
              .reduce((promise, item) => {
                return promise.then(() => {
                  return this.apiService.program.resources.instance.create({
                    id: res.data.payload.id,
                    sendEmail: true,
                    resourceTimeFrame: item.resourceTimeFrame,
                    eventResource: item.eventResource,
                    notes: item.notes,
                  });
                });
              }, this.$q.when(null))
              .then(
                () => {
                  this.apiService.program.instances
                    .detail(response.data.payload.id)
                    .then((response) => {
                      const res = {
                        data: {
                          payload: [response.data.payload],
                        },
                      };
                      deferred.resolve(res);
                    })
                    .catch((res) => {
                      deferred.reject(res);
                    });
                },
                (res) => {
                  deferred.reject(res);
                },
              );
          } else {
            this.apiService.program.instances
              .detail(res.data.payload.id)
              .then((response) => {
                const res = {
                  data: {
                    payload: [response.data.payload],
                  },
                };
                deferred.resolve(res);
              })
              .catch((res) => {
                deferred.reject(res);
              });
          }
        })
        .catch((res) => {
          deferred.reject(res);
        });
    } else {
      this.apiService.program.create(template, true).then((res) => {
        const item = res.data.payload;
        const data = [
          {
            eventTemplateId: item.id,
            categoryId: accountCategoryRelation.categoryId,
            discountId: template.discountId,
          },
        ];

        this.apiService.partners.templates
          .update({ eventTemplateId: item.id, data: data })
          .then((res) => {
            const startTime = new Date(item.startTime);
            const endTime = new Date(item.endTime);

            startTime.setHours(0);
            startTime.setMinutes(0);
            startTime.setSeconds(0);
            startTime.setMilliseconds(0);

            endTime.setHours(23);
            endTime.setMinutes(59);
            endTime.setSeconds(59);
            endTime.setMilliseconds(999);

            const data = {
              accountId,
              templateId: item.id,
              startTime: this.calendarService.toISO8601(startTime),
              endTime: this.calendarService.toISO8601(endTime),
            };

            this.apiService.partners.reseller
              .events(data)
              .then((res) => {
                deferred.resolve(res);
              })
              .catch((res) => {
                deferred.reject(res);
              });
          })
          .catch((res) => {
            deferred.reject(res);
          });
      });
    }

    return deferred.promise;
  }

  /**
   * Add a template event to the cart
   *   1. Create a template and publish an event
   *   2. Get the published event by the template id
   *   3. Get the event object needed by the cart
   *   4. Add the event object to the cart
   * @param template Template payload
   * @returns A promise object
   */
  addToCart(template) {
    const deferred = this.$q.defer<TODO>();

    this.apiService.program.create(template, true).then((res) => {
      this.apiService.event
        .getEventsByTemplateId(res.data.payload.id)
        .then((res) => {
          const payload = res.data.payload || [{}];
          const eventId = payload[0].id;

          if (!eventId) {
            deferred.reject(res);
          }
          this.apiService.event
            .detail(eventId)
            .then((res) => {
              const event = res.data.payload;
              this.cartService.events
                .addToCart(event)
                .then((res) => {
                  deferred.resolve(res);
                })
                .catch((res) => {
                  deferred.reject(res);
                });
            })
            .catch((res) => {
              deferred.reject(res);
            });
        })
        .catch((res) => {
          deferred.reject(res);
        });
    });
    return deferred.promise;
  }

  /**
   * Get Price list prices per priceList Id and discountId
   * @param priceList
   * @param discount
   * @returns [prices]
   */
  getDiscountedPriceList(priceList, discount) {
    const deferred = this.$q.defer<TODO>();

    if ((discount || {}).id && (priceList || {}).id) {
      this.apiService.discounts
        .preview({
          discountId: discount.id,
          priceListId: priceList.id,
        })
        .then((res) => {
          deferred.resolve(res.data.payload.prices);
        })
        .catch((err) => {
          deferred.resolve();
        });
    } else {
      return this.$q.when(null);
    }
    return deferred.promise;
  }
}

export const OrdersModalsPrivateEventServiceModule = angular
  .module('acme.view.application.orders.modals.privateEvent.services', [
    'acme.view.application.orders.modals.privateEvent.constants',
    'acme.common.service.calendar',
    'acme.common.service.templates.helpers',
  ])
  .service('PrivateEventService', PrivateEventService).name;
